import { ArrowRight, Check, ChevronDown, Code2, MessageSquare } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import { Button } from '@/shared/ui/button';

import { isCurrentFieldAcceptance } from '../field-current-evidence';
import { fieldNextStep } from '../field-next-step';
import { fieldToday } from '../field-overview';

import { FieldPersonaAvatar } from './field-persona-avatar';
import { FieldStatus, FieldTime } from './field-primitives';

import type { FieldDestination } from '../field-overview';
import type { FieldActorId, FieldCommand, FieldSnapshot } from '@mio/shared';
import type { KeyboardEvent } from 'react';

import './field-today.css';

interface TodayProps {
  snapshot: FieldSnapshot;
  unreadByActor: Partial<Record<FieldActorId, number>>;
  onNavigate: (destination: FieldDestination) => void;
  onOpenEditor: () => void;
  onAction?: (action: FieldCommand['action']) => Promise<boolean>;
  busy?: boolean;
}

const todayTabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'work', label: 'Work' },
  { id: 'team', label: 'Team' },
  { id: 'activity', label: 'Activity' },
] as const;
type TodayTab = (typeof todayTabs)[number]['id'];

function DeliveryOutcomes({ snapshot }: { snapshot: FieldSnapshot }) {
  const candidate =
    snapshot.prs.find((item) => item.id === snapshot.release.prId) ??
    [...snapshot.prs].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  return (
    <ul className="field-dashboard-outcomes">
      {snapshot.discovery?.outcomes.map((outcome) => {
        const accepted =
          candidate &&
          snapshot.controls?.acceptances.some(
            (item) =>
              item.journey === outcome.id &&
              item.actorId === 'jordan' &&
              item.prId === candidate.id &&
              isCurrentFieldAcceptance(snapshot, item),
          );
        return (
          <li key={outcome.id}>
            <span className="field-dashboard-outcome-mark" data-accepted={Boolean(accepted)}>
              {accepted ? <Check size={13} aria-hidden="true" /> : <span />}
            </span>
            <div>
              <strong>{outcome.title}</strong>
              <span>{accepted ? 'Accepted on current candidate' : 'Evidence pending'}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function WorkingGuide({ discovery }: { discovery: boolean }) {
  const steps = discovery
    ? [
        {
          title: 'Investigate and agree',
          detail:
            'Read the brief, ask the right people, and make assumptions and scope decisions explicit.',
        },
        {
          title: 'Deliver a working increment',
          detail:
            'Own the plan, build the result, and share verified behavior with the people who will use and operate it.',
        },
        {
          title: 'Review and respond',
          detail:
            'Distinguish defects from new scope, resolve worthwhile findings, and demonstrate a reliable handoff.',
        },
      ]
    : [
        {
          title: 'Understand the assignment',
          detail: 'Read your tickets and ask the relevant colleague for context.',
        },
        {
          title: 'Build and verify',
          detail: 'Work in the editor, capture a pull request, and run checks.',
        },
        {
          title: 'Share the evidence',
          detail:
            'Request a review or deliver a document to its owner. Saving alone does not notify them.',
        },
      ];
  return (
    <details className="field-dashboard-guide">
      <summary>
        <span>How the engagement works</span>
        <ChevronDown size={16} aria-hidden="true" />
      </summary>
      <ol>
        {steps.map((step, index) => (
          <li key={step.title}>
            <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
            <div>
              <strong>{step.title}</strong>
              <p>{step.detail}</p>
            </div>
          </li>
        ))}
      </ol>
    </details>
  );
}

export function FieldToday({
  snapshot,
  unreadByActor,
  onNavigate,
  onOpenEditor,
  onAction,
  busy = false,
}: TodayProps) {
  const [activeTab, setActiveTab] = useState<TodayTab>('overview');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const id = useId();
  const discovery = snapshot.discovery;
  const work = fieldToday(snapshot);
  const next = fieldNextStep(snapshot, unreadByActor);
  const unresolved =
    discovery?.decisions.filter((item) => item.status === 'open' || item.status === 'proposed') ??
    [];
  const findings =
    discovery?.findings.filter(
      (item) => item.status !== 'resolved' && item.status !== 'deferred',
    ) ?? [];
  const unread = snapshot.actors.reduce(
    (total, actor) => total + (unreadByActor[actor.id] ?? 0),
    0,
  );
  const recentOperations = [...snapshot.operations].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );

  function selectTab(tab: TodayTab) {
    setActiveTab(tab);
    tabRefs.current[todayTabs.findIndex((item) => item.id === tab)]?.focus();
  }

  function handleTabKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const target =
      event.key === 'ArrowRight'
        ? (index + 1) % todayTabs.length
        : event.key === 'ArrowLeft'
          ? (index + todayTabs.length - 1) % todayTabs.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? todayTabs.length - 1
              : null;
    if (target === null) return;
    event.preventDefault();
    const tab = todayTabs[target];
    if (tab) {
      setActiveTab(tab.id);
      tabRefs.current[target]?.focus();
    }
  }

  return (
    <div className="field-dashboard">
      <header className="field-dashboard-header">
        <div>
          <p>
            {snapshot.engagement.title} <span aria-hidden="true">/</span> Day{' '}
            {snapshot.engagement.day}
            {discovery ? '' : ' of 10'}
          </p>
          <h2>Today</h2>
        </div>
        <Button variant="secondary" size="sm" onClick={onOpenEditor}>
          <Code2 size={15} aria-hidden="true" /> Open code editor
        </Button>
      </header>

      <div className="field-dashboard-tabs" role="tablist" aria-label="Today sections">
        {todayTabs.map((tab, index) => (
          <button
            key={tab.id}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={`${id}-${tab.id}-tab`}
            aria-controls={`${id}-${tab.id}-panel`}
            aria-selected={activeTab === tab.id}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => setActiveTab(tab.id)}
            onKeyDown={(event) => handleTabKey(event, index)}
          >
            {tab.label}
            {tab.id === 'team' && unread > 0 ? (
              <span className="field-dashboard-count" aria-label={`${unread} unread messages`}>
                {unread}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <section
        className="field-dashboard-panel"
        role="tabpanel"
        id={`${id}-overview-panel`}
        aria-labelledby={`${id}-overview-tab`}
        hidden={activeTab !== 'overview'}
        tabIndex={0}
      >
        <div className="field-dashboard-overview">
          <section className="field-dashboard-focus" aria-labelledby={`${id}-next-title`}>
            <p className="field-dashboard-eyebrow">Suggested next step</p>
            <h3 id={`${id}-next-title`}>{next.title}</h3>
            <p className="field-dashboard-description">{next.reason}</p>
            <Button onClick={() => onNavigate(next.destination)}>
              {next.label}
              <ArrowRight size={15} aria-hidden="true" />
            </Button>
          </section>
          <section className="field-dashboard-delivery" aria-labelledby={`${id}-delivery-title`}>
            <div className="field-dashboard-section-heading">
              <h3 id={`${id}-delivery-title`}>
                {discovery ? 'What you are delivering' : 'Your progress'}
              </h3>
              {discovery ? (
                <button
                  type="button"
                  onClick={() => onNavigate({ section: 'engagement', focusId: discovery.brief.id })}
                >
                  Read brief <ArrowRight size={14} aria-hidden="true" />
                </button>
              ) : null}
            </div>
            {discovery ? (
              <DeliveryOutcomes snapshot={snapshot} />
            ) : (
              <dl className="field-dashboard-progress">
                <div>
                  <dt>Tickets complete</dt>
                  <dd>
                    {work.done}
                    <span> / {snapshot.tickets.length}</span>
                  </dd>
                </div>
                <div>
                  <dt>Open pull requests</dt>
                  <dd>{snapshot.prs.filter((pr) => pr.status === 'open').length}</dd>
                </div>
                <div>
                  <dt>Background tasks</dt>
                  <dd>{work.running.length}</dd>
                </div>
              </dl>
            )}
            <button
              className="field-dashboard-text-action"
              type="button"
              onClick={() => onNavigate({ section: 'assessment' })}
            >
              <Check size={15} aria-hidden="true" />
              {discovery ? 'View readiness requirements' : 'View assessment requirements'}
            </button>
          </section>
        </div>

        <div className="field-dashboard-shortcuts" aria-label="Your work at a glance">
          <button type="button" onClick={() => selectTab('work')}>
            <span>Active work</span>
            <strong>
              {work.activeTickets.length} <span>tickets</span>
            </strong>
            <ArrowRight size={15} aria-hidden="true" />
          </button>
          {discovery ? (
            <>
              <button
                type="button"
                onClick={() => onNavigate({ section: 'engagement', focusId: unresolved[0]?.id })}
              >
                <span>Decisions to clarify</span>
                <strong>{unresolved.length}</strong>
                <ArrowRight size={15} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => onNavigate({ section: 'engagement', focusId: findings[0]?.id })}
              >
                <span>Findings to review</span>
                <strong>{findings.length}</strong>
                <ArrowRight size={15} aria-hidden="true" />
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => selectTab('team')}>
                <span>Unread conversations</span>
                <strong>{snapshot.actors.filter((actor) => unreadByActor[actor.id]).length}</strong>
                <ArrowRight size={15} aria-hidden="true" />
              </button>
              <button type="button" onClick={() => selectTab('activity')}>
                <span>Open incidents</span>
                <strong>{work.openIncidents.length}</strong>
                <ArrowRight size={15} aria-hidden="true" />
              </button>
            </>
          )}
        </div>
        <WorkingGuide discovery={Boolean(discovery)} />
        {!discovery && onAction && snapshot.engagement.status === 'active' ? (
          <section className="field-dashboard-discovery" aria-label="Discovery workflow">
            <div>
              <h3>A brief-led way to work</h3>
              <p>
                Keep your current work and add the brief, decisions, and findings. Plan your own
                increments around the delivery outcomes.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || !snapshot.enabled}
              onClick={() => void onAction({ type: 'discovery.start' })}
            >
              Use discovery workflow
            </Button>
          </section>
        ) : null}
      </section>

      <section
        className="field-dashboard-panel"
        role="tabpanel"
        id={`${id}-work-panel`}
        aria-labelledby={`${id}-work-tab`}
        hidden={activeTab !== 'work'}
        tabIndex={0}
      >
        <div className="field-dashboard-section-heading">
          <div>
            <h3>{discovery ? 'Current increment' : 'Current work'}</h3>
            <p>The tickets you can move forward now.</p>
          </div>
          <button type="button" onClick={() => onNavigate({ section: 'board' })}>
            Open board <ArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
        {work.activeTickets.length ? (
          <ul className="field-dashboard-list">
            {work.activeTickets.map((ticket) => (
              <li key={ticket.id}>
                <button
                  type="button"
                  onClick={() => onNavigate({ section: 'board', focusId: ticket.id })}
                >
                  <div>
                    <strong>{ticket.title}</strong>
                    <span>
                      {ticket.id} ·{' '}
                      {snapshot.actors.find((actor) => actor.id === ticket.owner)?.name ??
                        ticket.owner}
                      {discovery ? '' : ` · Day ${ticket.day}`}
                    </span>
                  </div>
                  <FieldStatus value={ticket.status} />
                  <ArrowRight size={15} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="field-dashboard-empty">
            <h4>
              {discovery ? 'Your next increment starts here' : 'No outstanding tickets today'}
            </h4>
            <p>
              {discovery
                ? 'Explore the brief, test your assumptions, and plan a useful piece of work when you have enough context.'
                : 'Review the board or catch up with your team before choosing what to tackle next.'}
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                onNavigate(
                  discovery
                    ? { section: 'engagement', focusId: discovery.brief.id }
                    : { section: 'board' },
                )
              }
            >
              {discovery ? 'Explore the brief' : 'Review the board'}
              <ArrowRight size={14} aria-hidden="true" />
            </Button>
          </div>
        )}
        {work.reviewRequests.length ? (
          <section className="field-dashboard-subsection" aria-labelledby={`${id}-reviews-title`}>
            <div className="field-dashboard-section-heading">
              <h3 id={`${id}-reviews-title`}>Changes requested</h3>
            </div>
            <ul className="field-dashboard-list">
              {work.reviewRequests.map((review) => (
                <li key={review.id}>
                  <button
                    type="button"
                    onClick={() => onNavigate({ section: 'reviews', focusId: review.id })}
                  >
                    <div>
                      <strong>{review.title}</strong>
                      <span>A reviewer has requested changes on the current revision.</span>
                    </div>
                    <ArrowRight size={15} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </section>

      <section
        className="field-dashboard-panel"
        role="tabpanel"
        id={`${id}-team-panel`}
        aria-labelledby={`${id}-team-tab`}
        hidden={activeTab !== 'team'}
        tabIndex={0}
      >
        <div className="field-dashboard-section-heading">
          <div>
            <h3>People & conversations</h3>
            <p>Share context with the person who owns the decision.</p>
          </div>
          <span>Unread on this device</span>
        </div>
        <ul className="field-dashboard-list field-dashboard-team">
          {snapshot.actors.map((actor) => (
            <li key={actor.id}>
              <button
                type="button"
                onClick={() => onNavigate({ section: 'inbox', actorId: actor.id })}
              >
                <FieldPersonaAvatar
                  actorId={actor.id}
                  name={actor.name}
                  className="field-dashboard-avatar"
                />
                <div>
                  <strong>
                    {actor.name}
                    <span className="field-dashboard-role">{actor.role}</span>
                  </strong>
                  <span>{actor.focus}</span>
                </div>
                {unreadByActor[actor.id] ? (
                  <span
                    className="field-dashboard-count"
                    aria-label={`${unreadByActor[actor.id]} unread messages`}
                  >
                    {unreadByActor[actor.id]}
                  </span>
                ) : (
                  <MessageSquare size={16} aria-hidden="true" />
                )}
                <ArrowRight size={15} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section
        className="field-dashboard-panel"
        role="tabpanel"
        id={`${id}-activity-panel`}
        aria-labelledby={`${id}-activity-tab`}
        hidden={activeTab !== 'activity'}
        tabIndex={0}
      >
        <div className="field-dashboard-section-heading">
          <div>
            <h3>In the background</h3>
            <p>Operations, incidents, and release requirements.</p>
          </div>
          <button type="button" onClick={() => onNavigate({ section: 'operations' })}>
            View operations <ArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
        {work.openIncidents.length || snapshot.release.blockers.length ? (
          <section className="field-dashboard-subsection" aria-labelledby={`${id}-attention-title`}>
            <h4 id={`${id}-attention-title`}>Needs attention</h4>
            <ul className="field-dashboard-list">
              {work.openIncidents.map((incident) => (
                <li key={incident.id}>
                  <button
                    type="button"
                    onClick={() => onNavigate({ section: 'operations', focusId: incident.id })}
                  >
                    <div>
                      <strong>{incident.title}</strong>
                      <span>Open incident</span>
                    </div>
                    <FieldStatus value={incident.status} />
                    <ArrowRight size={15} aria-hidden="true" />
                  </button>
                </li>
              ))}
              {snapshot.release.blockers.map((blocker) => (
                <li key={blocker}>
                  <button type="button" onClick={() => onNavigate({ section: 'operations' })}>
                    <div>
                      <strong>{blocker}</strong>
                      <span>Release requirement</span>
                    </div>
                    <ArrowRight size={15} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {recentOperations.length ? (
          <ul className="field-dashboard-list">
            {recentOperations.map((operation) => (
              <li key={operation.id}>
                <div className="field-dashboard-activity-row">
                  <div>
                    <strong>{operation.summary}</strong>
                    <span>
                      <FieldTime value={operation.createdAt} />
                    </span>
                  </div>
                  <FieldStatus value={operation.status} />
                </div>
              </li>
            ))}
          </ul>
        ) : !work.openIncidents.length && !snapshot.release.blockers.length ? (
          <div className="field-dashboard-empty">
            <h4>Nothing running in the background</h4>
            <p>
              Colleague responses, document preparation, and checks will appear here when they
              start.
            </p>
          </div>
        ) : null}
      </section>
    </div>
  );
}
